import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseDateStr(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
}

function toDateStr(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function dayKey({ y, m, d }) {
  return y * 10000 + m * 100 + d;
}

function todayParts() {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
}

function formatDisplay(dateStr) {
  const p = parseDateStr(dateStr);
  if (!p) return "";
  return new Date(p.y, p.m, p.d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Replaces the native <input type="date"> calendar popup -- which is the
 * browser/OS's own unstyled widget, same CSS-unreachable situation as the
 * native country-select popup CountrySelect.jsx already replaced -- with a
 * custom calendar panel matching the site's design. Same value/onChange
 * contract as the native input it replaces: `value`/`onChange` carry plain
 * "YYYY-MM-DD" strings, `min` is a "YYYY-MM-DD" floor (days before it are
 * shown disabled) -- so BookingModal's date-change handling, and the
 * availability fetch keyed off that string, are both untouched. UI only.
 */
export default function DatePicker({ id, testId, value, onChange, min, placeholder = "Select a date", hasError }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const selected = parseDateStr(value);
  const minParsed = parseDateStr(min);
  const today = todayParts();

  const [view, setView] = useState(() => {
    const base = selected || minParsed || today;
    return { y: base.y, m: base.m };
  });

  const updateRect = () => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    if (selected) setView({ y: selected.y, m: selected.m });
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cells = useMemo(() => {
    const firstWeekday = new Date(view.y, view.m, 1).getDay();
    const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
    const list = [];
    for (let i = 0; i < firstWeekday; i++) list.push(null);
    for (let d = 1; d <= daysInMonth; d++) list.push(d);
    while (list.length % 7 !== 0) list.push(null);
    return list;
  }, [view]);

  const minKey = minParsed ? dayKey(minParsed) : null;
  const selectedKey = selected ? dayKey(selected) : null;
  const todayKeyVal = dayKey(today);

  const pick = (d) => {
    onChange(toDateStr(view.y, view.m, d));
    setOpen(false);
  };

  const panelStyle = useMemo(() => {
    if (!rect) return { display: "none" };
    const width = 288;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : width;
    const left = Math.min(rect.left, viewportWidth - width - 8);
    return { position: "fixed", top: rect.bottom + 6, left: Math.max(8, left), width };
  }, [rect]);

  const fieldBase =
    "w-full flex items-center gap-2 rounded-md px-3.5 py-2.5 text-sm bg-white border text-left focus:outline-none focus:ring-2 focus:ring-royal/30 focus:border-royal";

  return (
    <div>
      <button
        type="button"
        id={id}
        data-testid={testId}
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={Boolean(hasError)}
        className={`${fieldBase} ${hasError ? "border-red-400" : "border-line"} ${value ? "text-ink" : "text-ink-soft/70"}`}
      >
        <CalendarDays aria-hidden size={16} className="text-royal-deep flex-shrink-0" />
        <span className="flex-1 truncate">{value ? formatDisplay(value) : placeholder}</span>
        <ChevronDown aria-hidden size={14} className="text-ink-soft flex-shrink-0" />
      </button>

      {open &&
        createPortal(
          <div ref={panelRef} style={panelStyle} className="zt-date-popover">
            <div className="zt-date-header">
              <button type="button" onClick={() => setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))} aria-label="Previous month">
                <ChevronLeft size={16} />
              </button>
              <span>{MONTH_LABELS[view.m]} {view.y}</span>
              <button type="button" onClick={() => setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))} aria-label="Next month">
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="zt-date-weekdays">
              {WEEKDAY_LABELS.map((w) => <span key={w}>{w}</span>)}
            </div>
            <div className="zt-date-grid">
              {cells.map((d, i) => {
                if (d === null) return <span key={`blank-${i}`} />;
                const key = view.y * 10000 + view.m * 100 + d;
                const disabled = minKey !== null && key < minKey;
                const isSelected = key === selectedKey;
                const isToday = key === todayKeyVal;
                return (
                  <button
                    type="button"
                    key={d}
                    disabled={disabled}
                    onClick={() => pick(d)}
                    className={`zt-date-cell ${isSelected ? "is-selected" : ""} ${isToday && !isSelected ? "is-today" : ""}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
